import {
	Dtos,
	Email,
	FirstName,
	formatAddress,
	IndividualContactId,
	LastName,
	NameSuffix,
	SuretyAccountId,
} from "@inrev/common";
import {
	type AdminAccountIndividual,
	useAdminUpsertIndividualContact,
} from "@portal/api/account/admin";
import { Modal } from "@portal/components/layout/Modal";
import { ModalItemWithHeader } from "@portal/components/layout/ModalItemWithHeader";
import { useEffect, useMemo, useState } from "react";
import { type FieldError, useController, useForm } from "react-hook-form";
import { useCustomCompareMemo } from "use-custom-compare";
import { z } from "zod";
import { useAddressValidation } from "../../../../../api";
import { stripEmptyResolver, typedZodResolver } from "../../../../../utils/form";
import { uniqueSSNSchema } from "../../../../../validation";
import { AdminIndividualForm } from "./AdminIndividualForm";
import { AdminIndividualSpouseForm } from "./AdminSpouseForm";

export type DefaultAdminIndividualFormData = Omit<
	z.input<typeof Dtos.Admin.IndividualContact.Create.Request.schema>,
	"ssn" | "married" | "ownsAHome" | "includeInUnderwriting" | "includeInIndemnity"
> & {
	address: string | undefined;
	ssn: string | undefined;
	married: boolean | "";
	ownsAHome: boolean | "";
	includeInIndemnity: boolean | "";
	includeInUnderwriting: boolean | "";
};

const getDefaultFormData = (): DefaultAdminIndividualFormData => ({
	firstName: "" as FirstName,
	middleInitial: "",
	lastName: "" as LastName,
	suffix: "" as NameSuffix,
	address: "",
	email: "" as Email,
	ssn: "",
	married: "",
	ownsAHome: "",
	includeInIndemnity: "",
	includeInUnderwriting: "",
});

const individualToFormData = (
	selectedIndividual: AdminAccountIndividual,
): DefaultAdminIndividualFormData => ({
	firstName: selectedIndividual.firstName,
	middleInitial: selectedIndividual.middleInitial,
	lastName: selectedIndividual.lastName,
	suffix: selectedIndividual.suffix,
	address: selectedIndividual.address ? formatAddress(selectedIndividual.address) : "",
	email: selectedIndividual.email,
	ssn: selectedIndividual.ssn,
	married: selectedIndividual.spouse !== undefined,
	ownsAHome: selectedIndividual.ownsAHome ?? "",
	includeInIndemnity: selectedIndividual.config?.contract.includeInIndemnity ?? "",
	includeInUnderwriting: selectedIndividual.config?.contract.includeInUnderwriting ?? "",
});

export const getOwnerSchema = (ssns: string[]) =>
	Dtos.Admin.IndividualContact.Create.Request.schema.extend({
		ssn: uniqueSSNSchema(ssns).nullish(),
		// Blank / untouched YesNo is "" → stripped to undefined before parse; treat as not married.
		married: z.boolean().default(false),
	});

export type AdminIndividualOwnerFormData = z.infer<ReturnType<typeof getOwnerSchema>>;

export const getSpouseSchema = (ssns: string[]) =>
	Dtos.Admin.IndividualContact.Create.Request.schema.omit({ spouse: true, married: true }).extend({
		ssn: uniqueSSNSchema(ssns).nullish(),
	});
export type AdminIndividualSpouseFormData = z.infer<ReturnType<typeof getSpouseSchema>>;

type AdminIndividualModalProps = {
	accountId: SuretyAccountId;
	individuals: AdminAccountIndividual[];
	selectedIndividualId?: IndividualContactId;
	onClose: () => void;
};

export const AdminIndividualModal = ({
	accountId,
	individuals,
	selectedIndividualId: selectedOwnerId,
	onClose,
}: AdminIndividualModalProps) => {
	const {
		upsertIndividualContact,
		upsertIndividualContactIsLoading,
		upsertIndividualContactSuccess,
	} = useAdminUpsertIndividualContact(accountId, selectedOwnerId);
	const individualSSNs = useMemo(
		() =>
			individuals
				.map((individual) => individual.ssn)
				.filter((ssn) => ssn !== undefined) as string[],
		[individuals],
	);

	const selectedOwner =
		selectedOwnerId !== undefined
			? individuals.find((individual) => individual.id === selectedOwnerId)
			: undefined;
	const selectedOwnerFilteredSSNs = useMemo(
		() =>
			selectedOwner !== undefined
				? individualSSNs.filter((ssn) => ssn !== selectedOwner.ssn)
				: individualSSNs,
		[selectedOwner, individualSSNs],
	);
	const ownerSchema = useMemo(
		() => getOwnerSchema(selectedOwnerFilteredSSNs),
		[selectedOwnerFilteredSSNs],
	);
	const ownerDefaultValues =
		selectedOwner !== undefined ? individualToFormData(selectedOwner) : getDefaultFormData();
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const ownerForm = useForm<DefaultAdminIndividualFormData, any, AdminIndividualOwnerFormData>({
		defaultValues: ownerDefaultValues,
		resolver: stripEmptyResolver(typedZodResolver(ownerSchema)),
		reValidateMode: "onBlur",
	});

	const ownerAddressController = useController({
		name: "address",
		control: ownerForm.control,
	});

	const ownerAddressValidation = useAddressValidation({
		value: ownerForm.watch("address"),
		error: ownerAddressController.fieldState.error,
		setValue: (value: string) => ownerForm.setValue("address", value),
		setError: (error: FieldError) => ownerForm.setError("address", error),
		clearError: () => ownerForm.clearErrors("address"),
	});
	const [validatedOwnerData, setValidatedOwnerData] = useState<
		AdminIndividualOwnerFormData | undefined
	>();
	const ownerSSN = ownerForm.watch("ssn");
	const ownerOwnsAHome = ownerForm.watch("ownsAHome");
	const ownerIncludedInUnderwriting = ownerForm.watch("includeInUnderwriting");

	const selectedSpouse =
		selectedOwner !== undefined && selectedOwner.spouse !== undefined
			? individuals.find((i) => selectedOwner.spouse!.id === i.id)
			: undefined;
	const selectedSpouseFilteredSSNs = useMemo(
		() =>
			selectedSpouse !== undefined
				? individualSSNs.filter((ssn) => ssn !== selectedSpouse.ssn)
				: individualSSNs,
		[selectedSpouse, individualSSNs],
	);
	const spouseSchema = useCustomCompareMemo(
		() =>
			getSpouseSchema([
				...selectedSpouseFilteredSSNs,
				...(ownerSSN !== undefined ? ownerSSN && [ownerSSN] : []),
			]),
		[{ ownerSSN }],
		(prev, next) => {
			return prev[0].ownerSSN === next[0].ownerSSN || next[0].ownerSSN?.length !== 9;
		},
	);
	const spouseDefaultValues =
		selectedSpouse !== undefined ? individualToFormData(selectedSpouse) : getDefaultFormData();
	// biome-ignore lint/suspicious/noExplicitAny: legacy any
	const spouseForm = useForm<DefaultAdminIndividualFormData, any, AdminIndividualSpouseFormData>({
		defaultValues: spouseDefaultValues,
		resolver: stripEmptyResolver(typedZodResolver(spouseSchema)),
		reValidateMode: "onBlur",
	});

	const spouseAddressController = useController({
		name: "address",
		control: spouseForm.control,
	});

	const spouseAddressValidation = useAddressValidation({
		value: spouseForm.watch("address"),
		error: spouseAddressController.fieldState.error,
		setValue: (value: string) => spouseForm.setValue("address", value),
		setError: (error: FieldError) => spouseForm.setError("address", error),
		clearError: () => spouseForm.clearErrors("address"),
	});
	const spouseSSN = spouseForm.watch("ssn");
	const spouseIncludedInUnderwriting = spouseForm.watch("includeInUnderwriting");

	const currentForm = useMemo(
		() => (validatedOwnerData ? "spouse" : "owner"),
		[validatedOwnerData],
	);

	const handleSubmit = (
		ownerFormData: AdminIndividualOwnerFormData,
		spouseFormData?: AdminIndividualSpouseFormData,
	) => {
		if (ownerFormData.married) {
			if (!spouseFormData) throw new Error();
			ownerFormData = { ...ownerFormData, spouse: spouseFormData };
		}
		upsertIndividualContact(ownerFormData);
	};

	const onOwnerFormSubmit = (data: AdminIndividualOwnerFormData) => {
		if (data.married) setValidatedOwnerData(data);
		else handleSubmit(data);
	};

	const onSpouseFormSubmit = (data: AdminIndividualSpouseFormData) => {
		if (!validatedOwnerData) throw new Error();
		handleSubmit(validatedOwnerData, data);
	};

	useEffect(() => {
		if (upsertIndividualContactSuccess) onClose();
	}, [upsertIndividualContactSuccess]);

	useEffect(() => {
		if (validatedOwnerData) {
			spouseForm.setValue("ownsAHome", ownerOwnsAHome);
		}
	}, [validatedOwnerData]);

	useEffect(() => {
		if (selectedOwner !== undefined) {
			if (!ownerIncludedInUnderwriting) {
				ownerForm.setValue("ssn", undefined);
			} else if (!ownerForm.getValues("ssn")) {
				ownerForm.setValue("ssn", selectedOwner.ssn || "");
			}
		}
	}, [ownerIncludedInUnderwriting, selectedOwner]);

	useEffect(() => {
		if (selectedSpouse === undefined && spouseIncludedInUnderwriting && spouseSSN !== "") {
			spouseForm.setValue("ssn", undefined);
		} else if (
			selectedSpouse !== undefined &&
			spouseIncludedInUnderwriting &&
			!spouseForm.getValues("ssn")
		) {
			spouseForm.setValue("ssn", selectedSpouse.ssn || "");
		}
	}, [spouseIncludedInUnderwriting, selectedSpouse]);

	return (
		<Modal onClickOutside={onClose}>
			<ModalItemWithHeader
				header={currentForm === "owner" ? "New Individual" : "New Individual Spouse"}
				onClose={onClose}
			>
				<div className="max-w-[820px] px-[40px] pt-[25px] pb-[35px]">
					{currentForm === "owner" && (
						<AdminIndividualForm
							form={ownerForm}
							addressValidation={ownerAddressValidation}
							onSubmit={onOwnerFormSubmit}
							loading={upsertIndividualContactIsLoading}
						/>
					)}
					{currentForm === "spouse" && (
						<AdminIndividualSpouseForm
							form={spouseForm}
							addressValidation={spouseAddressValidation}
							validatedOwnerData={validatedOwnerData}
							onBack={() => setValidatedOwnerData(undefined)}
							onSubmit={onSpouseFormSubmit}
							loading={upsertIndividualContactIsLoading}
						/>
					)}
				</div>
			</ModalItemWithHeader>
		</Modal>
	);
};
